/**
 * useCampaignRanking
 *
 * Feature hook backing the campaign ranking sheet. Resolves the campaign's
 * leaderboard type, fetches the matching ranking list (user vs content), and
 * exposes the loading / empty state plus the "is this the current user" helper
 * the row components (Task 10) use to render the "You" label.
 *
 * Mirrors Flutter's `CampaignRankingBottomSheetState`
 * (lib/presentation/bottom_sheet/campaign_ranking_bottom_sheet.dart):
 * - `:50` — `_campaignType = CampaignType.values[campaign.type]` → here the
 *   entity already carries the resolved `campaignType`.
 * - `:96-145` — `_getData()`: early-return when already fetching OR
 *   `campaign.startDate > now`; set `_isFetching`; switch on the campaign type
 *   to call the matching ranking source; clear `_isFetching`.
 *
 * Architecture: Component → Hook → UseCase → Service → Data layer. The hook
 * stays free of `useBasePage` and free of UI; the sheet content component drives
 * the lifecycle (`fetchRanking()` on mount).
 */
import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/presentation/store';
import { CampaignType } from '@/domain/enums/campaign/campaignType';
import { GetCampaignRankingUsersMostSubscribersUseCase } from '@/domain/usecases/campaign/getCampaignRankingUsersMostSubscribersUseCase';
import { GetCampaignRankingContentMostLikesUseCase } from '@/domain/usecases/campaign/getCampaignRankingContentMostLikesUseCase';
import { GetCampaignRankingContentMostCoinsUseCase } from '@/domain/usecases/campaign/getCampaignRankingContentMostCoinsUseCase';
import type { CampaignEntity } from '@/domain/entities/campaign/campaignEntity';
import type { CampaignRankingUserEntity } from '@/domain/entities/campaign/campaignRankingUserEntity';
import type { CampaignRankingContentEntity } from '@/domain/entities/campaign/campaignRankingContentEntity';

export interface UseCampaignRankingResult {
  /** The campaign's leaderboard type, resolved from `campaign.type`. */
  campaignType: CampaignType;
  /** User leaderboard rows (populated only for `usersMostSubscribers`). */
  users: CampaignRankingUserEntity[];
  /** Content leaderboard rows (populated for `contentMostLikes` / `contentMostCoins`). */
  content: CampaignRankingContentEntity[];
  /** True while the initial fetch is in flight (mirrors Flutter `_isFetching`). */
  isFetching: boolean;
  /** True once fetching has settled with no rows in either list. */
  isEmpty: boolean;
  /** Fetches the matching leaderboard. Safe to call once on mount. */
  fetchRanking: () => Promise<void>;
  /** Whether `firebaseId` is the signed-in user (for the "You" row label). */
  isCurrentUser: (firebaseId: string) => boolean;
}

export const useCampaignRanking = (campaign: CampaignEntity): UseCampaignRankingResult => {
  const campaignType = campaign.campaignType;

  const [users, setUsers] = useState<CampaignRankingUserEntity[]>([]);
  const [content, setContent] = useState<CampaignRankingContentEntity[]>([]);
  // Init `true` so the sheet opens on the loading state, matching Flutter's
  // `_getData()` flipping `_isFetching` before the first frame settles.
  const [isFetching, setIsFetching] = useState(true);

  const currentUser = useSelector((state: RootState) => state.auth.user);
  const currentUserId = currentUser?.firebaseId ?? null;

  const fetchRanking = useCallback(async () => {
    // Mirrors Flutter `:97-100`: bail if the campaign has not started yet
    // (no re-entrancy guard needed here — the content component calls this once
    // on mount, and React state already gates a repeat fetch).
    if (campaign.startDate > Date.now()) {
      setIsFetching(false);
      return;
    }

    setIsFetching(true);

    switch (campaignType) {
      case CampaignType.usersMostSubscribers: {
        const result = await new GetCampaignRankingUsersMostSubscribersUseCase().execute(campaign);
        setUsers(result);
        break;
      }
      case CampaignType.contentMostLikes: {
        const result = await new GetCampaignRankingContentMostLikesUseCase().execute(campaign);
        setContent(result);
        break;
      }
      case CampaignType.contentMostCoins: {
        const result = await new GetCampaignRankingContentMostCoinsUseCase().execute(campaign);
        setContent(result);
        break;
      }
    }

    setIsFetching(false);
  }, [campaign, campaignType]);

  const isCurrentUser = useCallback(
    (firebaseId: string): boolean => currentUserId !== null && firebaseId === currentUserId,
    [currentUserId],
  );

  const isEmpty = users.length === 0 && content.length === 0;

  return {
    campaignType,
    users,
    content,
    isFetching,
    isEmpty,
    fetchRanking,
    isCurrentUser,
  };
};
